const SECOND_SIDE_BAR_WRAPPER_ID: string = "secondSidebarWrapper";
const PAGE_CONTENT_ID: string = "pageContent";

export interface SidebarListEntry {
  label: string;
  detailHtml?: string;
  onSelect?: (detailEl: HTMLElement | null) => void;
}

export abstract class BasePageComponent {
  public abstract getAppRoute(): string;

  public abstract getPageTitle(): string | null;

  //

  protected _pageContent!: HTMLDivElement;

  public init(): void {
    const secondSidebarWrapper = this._getHtmlDivElement(
      SECOND_SIDE_BAR_WRAPPER_ID,
    );
    this._pageContent = this._getHtmlDivElement(PAGE_CONTENT_ID);
    //
    secondSidebarWrapper.innerHTML = "";
    this._pageContent.innerHTML = "";
    this._pageContent.className = "sx-page";
    //
    this.afterInit(secondSidebarWrapper, this._pageContent);
    this.renderLeftMenuContent(secondSidebarWrapper);
    this.renderPageContent(this._pageContent);
  }

  public dispose(): void {
    const secondSidebarWrapper = this._getHtmlDivElement(
      SECOND_SIDE_BAR_WRAPPER_ID,
    );
    const pageContent = this._getHtmlDivElement(
      PAGE_CONTENT_ID,
    )! as HTMLDivElement;
    //
    this.beforeDispose(secondSidebarWrapper, pageContent);
    secondSidebarWrapper.innerHTML = "";
    secondSidebarWrapper.classList.remove("open", "close");
    pageContent.innerHTML = "";
    pageContent.className = "";
  }

  //

  protected afterInit(
    _secondSidebarWrapper: HTMLDivElement,
    _pageContent: HTMLDivElement,
  ): void {}

  protected beforeDispose(
    _secondSidebarWrapper: HTMLDivElement,
    _pageContent: HTMLDivElement,
  ): void {}

  protected renderLeftMenuContent(
    _secondSidebarWrapper: HTMLDivElement,
  ): void {}

  protected renderPageContent(_pageContent: HTMLDivElement): void {}

  protected navigate(route: string): void {
    document.dispatchEvent(new CustomEvent("app:navigate", { detail: route }));
  }

  protected _setupSidebar(
    wrapper: HTMLDivElement,
    title: string,
    actions: Array<{ icon: string; title: string; cssClass?: string; onClick: () => void }>,
    onBack?: () => void,
  ): HTMLDivElement {
    const backHtml = onBack
      ? `<button class="aplic-back-btn" title="Volver"><i class="ph ph-arrow-left"></i></button>`
      : "";
    const actionsHtml = actions
      .map((a) => `<button class="aplic-create-btn${a.cssClass ? ` ${a.cssClass}` : ""}" title="${a.title}"><i class="ph ${a.icon}"></i></button>`)
      .join("");

    wrapper.innerHTML = `
      <div class="aplic-sidebar-header">
        ${backHtml}
        <span class="aplic-sidebar-title">${title}</span>
        ${actionsHtml}
      </div>
      <div class="aplic-content"><div class="aplic-loading">Cargando...</div></div>
    `;

    if (onBack) wrapper.querySelector<HTMLButtonElement>(".aplic-back-btn")!.onclick = onBack;
    wrapper.querySelectorAll<HTMLButtonElement>(".aplic-create-btn").forEach((btn, i) => {
      btn.onclick = actions[i].onClick;
    });

    this.openSidebar(wrapper);
    return wrapper.querySelector<HTMLDivElement>(".aplic-content")!;
  }

  protected openSidebar(wrapper: HTMLDivElement): void {
    requestAnimationFrame(() => {
      const alreadyOpen = wrapper.classList.contains("open");
      wrapper.classList.add("open");
      if (alreadyOpen) {
        this.onSidebarOpen();
      } else {
        wrapper.addEventListener("transitionend", () => this.onSidebarOpen(), { once: true });
      }
    });
  }

  protected onSidebarOpen(): void {}

  /**
   * Andamiaje compartido del second sidebar: selector de año + lista de ítems
   * con detalle desplegable al hacer click. Cada página solo aporta los años y,
   * por año, las entradas (label + detalle + efecto en onSelect).
   */
  protected _renderYearGroupedList(
    container: HTMLElement,
    opts: {
      years: number[];
      getEntries: (year: number) => SidebarListEntry[];
      emptyText: string;
      onEmpty?: () => void;
    },
  ): void {
    if (opts.years.length === 0) {
      container.innerHTML = `<div class="aplic-loading">${opts.emptyText}</div>`;
      opts.onEmpty?.();
      return;
    }

    container.innerHTML = `
      <div class="aplic-year-picker">
        <select class="aplic-year-select">
          ${opts.years.map((y) => `<option value="${y}">${y}</option>`).join("")}
        </select>
      </div>
      <div class="aplic-items-list"></div>
    `;

    const yearSelect = container.querySelector<HTMLSelectElement>(".aplic-year-select")!;
    const itemsListEl = container.querySelector<HTMLDivElement>(".aplic-items-list")!;

    const renderEntries = (year: number) => {
      const entries = opts.getEntries(year);
      itemsListEl.innerHTML = entries
        .map((e) => `<div class="aplic-item">${e.label}</div>`)
        .join("");
      itemsListEl.querySelectorAll<HTMLElement>(".aplic-item").forEach((el, i) => {
        el.onclick = () => {
          itemsListEl.querySelectorAll<HTMLElement>(".aplic-item").forEach((x) => x.classList.remove("active"));
          itemsListEl.querySelectorAll<HTMLElement>(".aplic-item-detail").forEach((d) => d.remove());
          el.classList.add("active");
          const entry = entries[i];
          let detailEl: HTMLElement | null = null;
          if (entry.detailHtml) {
            detailEl = document.createElement("div");
            detailEl.className = "aplic-item-detail";
            detailEl.innerHTML = entry.detailHtml;
            el.insertAdjacentElement("afterend", detailEl);
          }
          entry.onSelect?.(detailEl);
        };
      });
      itemsListEl.querySelector<HTMLElement>(".aplic-item")?.click();
    };

    yearSelect.onchange = () => renderEntries(Number(yearSelect.value));
    renderEntries(opts.years[0]);
  }

  protected closeSecondSidebarWrapper(): Promise<void> {
    const secondSidebarWrapper = this._getHtmlDivElement(
      SECOND_SIDE_BAR_WRAPPER_ID,
    );
    const openCssClass = "open";

    if (!secondSidebarWrapper.classList.contains(openCssClass)) {
      return Promise.resolve();
    }

    const closeCssClass = "close";
    secondSidebarWrapper.classList.add(closeCssClass);

    return new Promise<void>((resolve) => {
      setTimeout(() => {
        secondSidebarWrapper.classList.remove(openCssClass);
        secondSidebarWrapper.classList.remove(closeCssClass);
        secondSidebarWrapper.innerHTML = "";
        resolve();
      }, 300);
    });
  }

  //

  private _getHtmlDivElement(id: string): HTMLDivElement {
    const htmlDivElement: HTMLDivElement | null = document.getElementById(
      id,
    ) as HTMLDivElement;
    //
    if (!htmlDivElement) {
      throw Error(`HTMLDivElement.id:${id} no encontrado`);
    }
    //
    return htmlDivElement;
  }
}
